/**
 * ============================================================
 * tile.co.uk — Cart Adhesive Recommendation Engine
 * ============================================================
 * Reads tile products in the cart via the Shopify Cart API,
 * calculates total m² per tile line item, maps material to
 * the correct adhesive recommendation, and renders a
 * suggestion panel inside the cart drawer.
 *
 * DEPENDENCIES:
 * - tile-metafield-cache.liquid must be rendered on product
 *   pages to populate the metafield cache.
 * - The cart drawer must have an element with
 *   id="adhesive-recommendation-target" where the panel
 *   will be injected.
 *
 * DEVELOPER NOTES:
 * - This script is self-contained and uses no external libraries.
 * - Call TileAdhesiveRecommender.init() once the cart drawer
 *   opens or cart contents change.
 * ============================================================
 */

var TileAdhesiveRecommender = (function () {

  // ============================================================
  // CONFIG
  // ============================================================
  var CONFIG = {
    shopDomain: window.Shopify && window.Shopify.shop ? window.Shopify.shop : 'tile-co-uk.myshopify.com',
    targetElementId: 'adhesive-recommendation-target',
    minimumM2: 0.1,
  };


  // ============================================================
  // MATERIAL + WALL/FLOOR → ADHESIVE TYPE LOOKUP
  // Maps material value (lowercase) to an adhesive category.
  // Ceramic is split by wall/floor — all others use one type.
  // ============================================================
  var MATERIAL_MAP = {
    'ceramic':                   { wall: 'ceramic_wall', floor: 'ceramic_floor' },
    'fully vitrified ceramic':   { wall: 'ceramic_wall', floor: 'ceramic_floor' },
    'porcelain':                 'porcelain',
    'glazed porcelain':          'porcelain',
    // 'glass': 'glass',  // Removed — no suitable stocked adhesive for glass tiles currently
    'limestone':                 'moisture_sensitive_stone',
    'marble':                    'moisture_sensitive_stone',
    'travertine':                'moisture_sensitive_stone',
    'sandstone':                 'moisture_sensitive_stone',
    'slate':                     'natural_stone',
    'quartzite':                 'natural_stone',
    'terracotta':                'natural_stone',
  };


  // ============================================================
  // ADHESIVE TYPE → PRODUCT RECOMMENDATION
  // ============================================================
  var ADHESIVE_RECOMMENDATIONS = {

    ceramic_wall: {
      label:              'Ceramic wall tiles',
      product_handle:     'bal-15kg-green-star-wall-tile-adhesive',
      product_name:       'BAL Green Star',
      colour:             'grey',
      colour_note:        null,
      coverage_m2_per_unit: 6.0,   // 0.4 m²/kg dry wall × 15kg per BAL datasheet
      bags_kg:            15,
      bag_label:          'tub',
      note:               'Ready-mixed — no mixing required. Suitable for ceramic wall tiles including bathrooms and shower areas.',
    },

    ceramic_floor: {
      label:              'Ceramic floor tiles',
      product_handle:     'kerakoll-setaflex-standard-set-s1-white-tile-adhesive-20kg',
      product_name:       'Kerakoll Setaflex Standard Set',
      colour:             'white',
      colour_note:        null,
      coverage_kg_per_m2: 3.6,  // 1.2 kg/m²/mm × 3mm standard bed per Kerakoll datasheet
      bags_kg:            20,
      bag_label:          'bag',
      note:               'Cement-based powder adhesive — requires mixing with water on site.',
    },

    porcelain: {
      label:              'Porcelain tiles',
      product_handle:     'kerakoll-setaflex-rapid-set-s1-white-tile-adhesive-20kg',
      product_name:       'Kerakoll Setaflex Rapid Set',
      colour:             'white',
      colour_note:        null,
      coverage_kg_per_m2: 3.6,  // 1.2 kg/m²/mm × 3mm standard bed per Kerakoll datasheet
      bags_kg:            20,
      bag_label:          'bag',
      note:               'Cement-based powder adhesive — requires mixing with water on site.',
    },

    glass: {
      label:              'Glass tiles',
      product_handle:     'bal-10ltr-white-star-plus-wall-tile-adhesive',
      product_name:       'BAL White Star Plus',
      colour:             'white',
      colour_note:        'White adhesive is essential for glass and translucent tiles to prevent the adhesive showing through.',
      coverage_m2_per_unit: 5.0,  // 0.5 m²/litre × 10 litres wet wall per BAL datasheet
      bags_kg:            10,
      bag_label:          'tub',
      note:               'Ready-mixed — no mixing required.',
    },

    moisture_sensitive_stone: {
      label:              'Moisture-sensitive natural stone',
      product_handle:     'kerakoll-h40-gel-c2-te-s1-standard-set-flexible-tile-adhesive-20kg',
      product_name:       'Kerakoll H40 Gel',
      colour:             'white',
      colour_note:        'White adhesive is essential for limestone, marble and travertine to prevent water staining.',
      coverage_kg_per_m2: 3.6,  // 1.2 kg/m²/mm × 3mm standard bed per Kerakoll datasheet (white)
      bags_kg:            20,
      bag_label:          'bag',
      note:               'Moisture-sensitive stone such as limestone, marble, travertine and sandstone requires a specialist white adhesive to prevent staining.',
    },

    natural_stone: {
      label:              'Natural stone tiles',
      product_handle:     'kerakoll-h40-gel-c2-te-s1-standard-set-flexible-tile-adhesive-20kg',
      product_name:       'Kerakoll H40 Gel',
      colour:             'white',
      colour_note:        null,
      coverage_kg_per_m2: 3.6,  // 1.2 kg/m²/mm × 3mm standard bed per Kerakoll datasheet
      bags_kg:            20,
      bag_label:          'bag',
      note:               'Cement-based powder adhesive — requires mixing with water on site.',
    },

  };


  // ============================================================
  // WALL/FLOOR DETECTION
  // Returns true if this product is wall-only based on the
  // wall_or_floor metafield value.
  // Anything containing "Wall" but NOT "Floor" = wall only.
  // ============================================================
  function isWallOnly(wallOrFloor) {
    if (!wallOrFloor) return false;
    var v = wallOrFloor.toLowerCase();
    return v.indexOf('wall') !== -1 && v.indexOf('floor') !== -1 ? false : v.indexOf('wall') !== -1;
  }


  // ============================================================
  // SOLD-BY NORMALISATION
  // ============================================================
  function normaliseSoldBy(value) {
    if (!value) return null;
    var v = value.toLowerCase().trim();
    if (v === 'm2' || v === 'm²')          return 'm2';
    if (v === 'box' || v === 'pack')       return 'box';
    if (v === 'sheet' || v === 'mosaic')   return 'sheet';
    if (
      v === 'tile' || v === 'piece' ||
      v.indexOf('tile set') !== -1 ||
      v.indexOf('-tile set') !== -1
    )                                      return 'tile';
    return v;
  }


  // ============================================================
  // IS TILE PRODUCT
  // ============================================================
  function isTileProduct(soldBy) {
    var norm = normaliseSoldBy(soldBy);
    return norm === 'box' || norm === 'sheet' || norm === 'tile' || norm === 'm2';
  }


  // ============================================================
  // CALCULATE M²
  // ============================================================
  function calculateM2(quantity, calculatorValue, soldBy) {
    var norm = normaliseSoldBy(soldBy);
    if (!calculatorValue || isNaN(calculatorValue) || calculatorValue <= 0) {
      return 0;
    }
    if (norm === 'm2') {
      return quantity;
    }
    var m2PerUnit = 1 / calculatorValue;
    return quantity * m2PerUnit;
  }


  // ============================================================
  // CALCULATE BAGS NEEDED
  // Supports two calculation methods:
  // 1. coverage_kg_per_m2 — cement powder products (kg per m²)
  // 2. coverage_m2_per_unit — ready-mixed products (m² per tub)
  // Adds 10% buffer to account for waste and cuts.
  // ============================================================
  function calculateBags(totalM2, adhesive) {
    if (totalM2 <= 0) return 0;
    if (adhesive.coverage_m2_per_unit) {
      // Ready-mixed: divide m² by coverage per tub
      return Math.ceil((totalM2 * 1.1) / adhesive.coverage_m2_per_unit);
    }
    // Powder: multiply m² by kg/m², divide by bag size
    var totalKgNeeded = totalM2 * adhesive.coverage_kg_per_m2 * 1.1;
    return Math.ceil(totalKgNeeded / adhesive.bags_kg);
  }


  // ============================================================
  // GET ADHESIVE TYPE FROM MATERIAL + WALL/FLOOR + THICKNESS
  // Returns null for 20mm tiles — these are outdoor paving tiles
  // and no suitable stocked adhesive is available for them.
  // ============================================================
  function getAdhesiveType(material, wallOrFloor, tags, thickness) {
    if (!material) return null;

    // Exclude 20mm porcelain tiles — these are outdoor paving tiles,
    // not suitable for standard adhesives. Terracotta and other
    // materials at 20mm are indoor tiles and are not excluded.
    if (thickness) {
      var t = thickness.toString().replace(/\s/g, '').toLowerCase();
      var isPorcelain = material && (material.toLowerCase() === 'porcelain' || material.toLowerCase() === 'glazed porcelain');
      if ((t === '20mm' || t === '20') && isPorcelain) return null;
    }

    var key = material.toLowerCase().trim();
    var mapping = MATERIAL_MAP[key];
    if (!mapping) return null;
    // If mapping has wall/floor split, resolve it
    if (typeof mapping === 'object' && mapping.wall && mapping.floor) {
      return isWallOnly(wallOrFloor) ? mapping.wall : mapping.floor;
    }
    return mapping;
  }


  // ============================================================
  // FETCH PRODUCT METAFIELDS FROM CACHE
  // Reads from TileMetafieldCache (populated by
  // tile-metafield-cache.liquid on product pages) and
  // sessionStorage for products viewed earlier in the session.
  // ============================================================
  function fetchProductMetafields(productIds, callback) {
    var cache = {};

    try {
      var stored = JSON.parse(sessionStorage.getItem('TileMetafieldCache') || '{}');
      Object.keys(stored).forEach(function(id) { cache[id] = stored[id]; });
    } catch(e) {}

    if (window.TileMetafieldCache) {
      Object.keys(window.TileMetafieldCache).forEach(function(id) {
        cache[id] = window.TileMetafieldCache[id];
      });
    }

    var result = {};
    var missing = [];

    productIds.forEach(function(pid) {
      if (cache[pid]) {
        result[pid] = cache[pid];
      } else {
        missing.push(pid);
      }
    });

    if (missing.length > 0) {
      console.info(
        '[TileAdhesiveRecommender] ' + missing.length + ' product(s) not in metafield cache. ' +
        'IDs: ' + missing.join(', ')
      );
    }

    callback(null, result);
  }


  // ============================================================
  // ANALYSE CART
  // ============================================================
  function analyseCart(callback) {
    fetch('/cart.js')
      .then(function (res) { return res.json(); })
      .then(function (cart) {
        var productIds = [];

        cart.items.forEach(function (item) {
          var pid = String(item.product_id);
          if (productIds.indexOf(pid) === -1) {
            productIds.push(pid);
          }
        });

        if (productIds.length === 0) {
          return callback(null, []);
        }

        fetchProductMetafields(productIds, function (err, metafields) {
          if (err) return callback(err, null);

          var tileItems = [];

          cart.items.forEach(function (item) {
            var pid = String(item.product_id);
            var meta = metafields[pid];
            if (!meta) return;

            var soldBy = meta.sold_by;
            if (!isTileProduct(soldBy)) return;

            var calculatorValue = meta.calculator;
            var m2 = calculateM2(item.quantity, calculatorValue, soldBy);
            var adhesiveType = getAdhesiveType(meta.material, meta.wall_or_floor, meta.tags, meta.thickness);

            if (m2 > 0 && adhesiveType) {
              tileItems.push({
                title:         item.title,
                quantity:      item.quantity,
                sold_by:       soldBy,
                material:      meta.material,
                wall_or_floor: meta.wall_or_floor,
                m2:            m2,
                adhesive_type: adhesiveType,
              });
            }
          });

          // Also collect all product handles in the cart so we can
          // check if the recommended adhesive is already in there
          var cartHandles = cart.items.map(function(item) {
            return item.handle || (item.url ? item.url.replace('/products/', '').split('?')[0] : '');
          });

          callback(null, tileItems, cartHandles);
        });
      })
      .catch(function (err) {
        callback(err, null);
      });
  }


  // ============================================================
  // PRIORITY ORDER
  // When multiple tile types are in the cart, always recommend
  // up — never down.
  // ============================================================
  var PRIORITY = {
    moisture_sensitive_stone: 6,
    natural_stone:            5,
    glass:                    4,
    porcelain:                3,
    ceramic_floor:            2,
    ceramic_wall:             1,
  };


  // ============================================================
  // BUILD RECOMMENDATION
  // ============================================================
  function buildRecommendation(tileItems, cartHandles) {
    if (!tileItems || tileItems.length === 0) return null;
    cartHandles = cartHandles || [];

    // First pass — find the highest priority adhesive type needed
    var highestPriority = 0;
    var recommendedType = null;

    tileItems.forEach(function (item) {
      var p = PRIORITY[item.adhesive_type] || 0;
      if (p > highestPriority) {
        highestPriority = p;
        recommendedType = item.adhesive_type;
      }
    });

    if (!recommendedType) return null;

    var adhesive = ADHESIVE_RECOMMENDATIONS[recommendedType];
    if (!adhesive) return null;

    // If the recommended adhesive is already in the cart, hide the panel
    var adhesiveHandle = adhesive.product_handle;
    var alreadyInCart = cartHandles.some(function(handle) {
      return handle === adhesiveHandle;
    });
    if (alreadyInCart) return null;

    // Second pass — only sum m2 for tiles that need THIS adhesive type.
    // Tiles covered by a different adhesive are excluded so we don't
    // over-count when e.g. wall tiles already have adhesive in the cart.
    var totalM2 = 0;
    tileItems.forEach(function (item) {
      if (item.adhesive_type === recommendedType) {
        totalM2 += item.m2;
      }
    });

    if (totalM2 < CONFIG.minimumM2) return null;

    var bagsNeeded = calculateBags(totalM2, adhesive);

    return {
      total_m2:      Math.round(totalM2 * 100) / 100,
      adhesive_type: recommendedType,
      adhesive:      adhesive,
      bags_needed:   bagsNeeded,
    };
  }


  // ============================================================
  // ADD TO CART
  // Adds the recommended adhesive directly to the cart and
  // triggers Noor's native cart drawer re-render so the new
  // line item appears and the recommendation panel disappears.
  // ============================================================
  function addToCart(variantId, quantity, buttonEl) {
    buttonEl.disabled = true;
    buttonEl.textContent = 'Adding…';

    // Get the cart drawer section ID the same way Noor does
    var cartDrawerSection = document.querySelector('[cart-drawer-section]');
    var sectionId = cartDrawerSection ? cartDrawerSection.dataset.id : null;

    var body = { id: variantId, quantity: quantity };

    // Include sections in the request so Noor can re-render
    // the cart drawer natively (same pattern as product-form-context)
    if (sectionId) {
      body.sections = [sectionId];
      body.sections_url = window.location.pathname;
    }

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify(body),
    })
    .then(function(res) { return res.json(); })
    .then(function(response) {
      if (response.status) {
        throw new Error(response.description || 'Could not add to cart');
      }

      // Use Noor native CartDrawer.renderContents() to re-render
      // the drawer — this updates the line items and wipes the
      // recommendation panel (which is inside the re-rendered HTML)
      var cartDrawerEl = document.querySelector('cart-drawer');
      if (cartDrawerEl && typeof cartDrawerEl.renderContents === 'function' && response.sections) {
        cartDrawerEl.renderContents(response);
        // Re-run recommender after drawer re-renders — adhesive has
        // no sold_by metafield so the panel will not reappear
        setTimeout(function() {
          init();
        }, 1400);
      } else {
        // Fallback: update button and cart count manually
        buttonEl.textContent = '✓ Added to cart';
        buttonEl.style.background = '#2d6a1f';
        fetch('/cart.js')
          .then(function(r) { return r.json(); })
          .then(function(cart) {
            var countEl = document.querySelector('[data-cart-count]');
            if (countEl) countEl.textContent = cart.item_count;
          });
      }
    })
    .catch(function() {
      buttonEl.disabled = false;
      buttonEl.textContent = 'Error — try again';
      buttonEl.style.background = '#c0392b';
    });
  }


  // ============================================================
  // FETCH PRODUCT DATA (variant ID, thumbnail, price)
  // Single fetch to the product JSON endpoint returns all three.
  // ============================================================
  function fetchProductData(handle, callback) {
    fetch('/products/' + handle + '.js')
      .then(function(res) { return res.json(); })
      .then(function(product) {
        var variant = product.variants && product.variants[0] ? product.variants[0] : null;
        callback(null, {
          variantId:     variant ? variant.id : null,
          price:         variant ? variant.price : null,  // price in pence
          featuredImage: product.featured_image || null,
          title:         product.title || '',
        });
      })
      .catch(function(err) { callback(err, null); });
  }


  // ============================================================
  // RENDER PANEL
  // ============================================================
  function renderPanel(recommendation) {
    var target = document.getElementById(CONFIG.targetElementId);
    if (!target) return;

    if (!recommendation) {
      target.innerHTML = '';
      return;
    }

    var a = recommendation.adhesive;
    var m2Display = recommendation.total_m2.toFixed(2);
    var bagsDisplay = recommendation.bags_needed;
    var bagLabel = a.bag_label || 'bag';
    var bagLabelPlural = bagsDisplay !== 1 ? bagLabel + 's' : bagLabel;

    var colourNote = a.colour_note
      ? '<p style="margin:6px 0 0;font-size:12px;color:#c47d00;">' + a.colour_note + '</p>'
      : '';

    // Unique button ID so we can attach the click handler after render
    var btnId = 'adhesive-add-btn-' + Date.now();
    var priceId = 'adhesive-price-' + btnId;

    var html = [
      '<div style="',
        'margin:16px 0 0;',
        'padding:14px 16px;',
        'background:#faf4f0;',
        'border:1px solid #e0d4c8;',
        'border-radius:8px;',
        'font-family:inherit;',
        'font-size:13px;',
        'color:#1a1a1a;',
        'line-height:1.5;',
      '">',

        // Header with trowel icon
        '<div style="display:flex;align-items:center;gap:8px;margin-bottom:10px;">',
          // Trowel SVG icon
          '<svg width="18" height="18" viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg">',
            // Handle
            '<rect x="3" y="2" width="5" height="13" rx="1.5" fill="#3c0000"/>',
            // Trowel body
            '<rect x="1" y="15" width="9" height="10" rx="1" fill="#3c0000"/>',
            // Notched teeth along bottom
            '<rect x="1" y="25" width="2" height="3" rx="0.5" fill="#3c0000"/>',
            '<rect x="4.5" y="25" width="2" height="3" rx="0.5" fill="#3c0000"/>',
            '<rect x="8" y="25" width="2" height="3" rx="0.5" fill="#3c0000"/>',
            // Adhesive ridges
            '<rect x="13" y="15" width="22" height="2" rx="1" fill="#3c0000" opacity="0.4"/>',
            '<rect x="13" y="19" width="22" height="2" rx="1" fill="#3c0000" opacity="0.4"/>',
            '<rect x="13" y="23" width="22" height="2" rx="1" fill="#3c0000" opacity="0.4"/>',
          '</svg>',
          '<span style="font-weight:600;font-size:13px;color:#3c0000;">You\'ll also need adhesive</span>',
        '</div>',

        // m² summary
        '<p style="margin:0 0 8px;font-size:12px;color:#888;">',
          'Based on <strong style="color:#1a1a1a;">' + m2Display + ' m²</strong> of tiles in your cart.',
        '</p>',

        // Product card with thumbnail placeholder
        '<div style="',
          'background:#fff;',
          'border:1px solid #e5e5e3;',
          'border-radius:6px;',
          'padding:10px 12px;',
          'margin-bottom:10px;',
          'display:flex;',
          'gap:12px;',
          'align-items:flex-start;',
        '">',
          // Thumbnail
          '<div id="adhesive-thumb-' + btnId + '" style="',
            'width:56px;',
            'height:56px;',
            'flex-shrink:0;',
            'border-radius:4px;',
            'background:#f0ebe5;',
            'overflow:hidden;',
          '"></div>',
          // Details
          '<div style="flex:1;min-width:0;">',
            '<div style="font-weight:600;font-size:13px;margin-bottom:2px;">' + a.product_name + '</div>',
            '<div style="font-size:12px;color:#888;margin-bottom:4px;">' + a.label + ' · ' + a.colour + '</div>',
            '<div style="font-size:13px;color:#1a1a1a;">',
              '<strong>' + bagsDisplay + ' ' + bagLabelPlural + '</strong>',
              ' recommended',
              ' <span style="font-size:11px;color:#aaa;">(inc. 10% for waste)</span>',
            '</div>',
            '<div id="' + priceId + '" style="font-size:13px;color:#1a1a1a;margin-top:4px;"></div>',
            colourNote,
          '</div>',
        '</div>',

        // Add to cart button
        '<button id="' + btnId + '" style="',
          'display:block;',
          'width:100%;',
          'text-align:center;',
          'background:#3c0000;',
          'color:#fff;',
          'font-size:13px;',
          'font-weight:600;',
          'padding:10px 16px;',
          'border-radius:6px;',
          'border:none;',
          'cursor:pointer;',
          'margin-top:10px;',
          'font-family:inherit;',
        '">',
          'Add ' + bagsDisplay + ' ' + bagLabelPlural + ' of ' + a.product_name + ' to cart',
        '</button>',

        // View product link
        '<p style="text-align:center;margin:8px 0 0;">',
          '<a href="/products/' + a.product_handle + '" style="font-size:12px;color:#3c0000;">',
            'View product details',
          '</a>',
        '</p>',

      '</div>',
    ].join('');

    target.innerHTML = html;

    // Fetch product data (variant ID, thumbnail, price) in one call
    fetchProductData(a.product_handle, function(err, data) {
      if (!err && data) {
        // Populate thumbnail
        var thumbEl = document.getElementById('adhesive-thumb-' + btnId);
        if (thumbEl && data.featuredImage) {
          var imgUrl = data.featuredImage.replace(/(\.[^.]+)$/, '_80x80_crop_center$1');
          thumbEl.innerHTML = '<img src="' + imgUrl + '" alt="' + data.title + '" style="width:100%;height:100%;object-fit:cover;">';
        }

        // Populate price — show total price for all bags
        var priceEl = document.getElementById(priceId);
        if (priceEl && data.price) {
          var unitPrice = data.price / 100;
          var totalPrice = (unitPrice * bagsDisplay).toFixed(2);
          var unitPriceDisplay = unitPrice.toFixed(2);
          priceEl.innerHTML = '<strong style="color:#3c0000;">£' + totalPrice + '</strong>'
            + ' <span style="font-size:11px;color:#aaa;">(£' + unitPriceDisplay + ' each)</span>';
        }

        // Wire up add to cart button
        if (data.variantId) {
          var btn = document.getElementById(btnId);
          if (btn) {
            btn.addEventListener('click', function() {
              addToCart(data.variantId, bagsDisplay, btn);
            });
          }
        }
      }
    });
  }


  // ============================================================
  // PUBLIC INIT
  // ============================================================
  function init() {
    analyseCart(function (err, tileItems, cartHandles) {
      if (err) {
        console.warn('[TileAdhesiveRecommender] Error fetching cart:', err);
        return;
      }
      var recommendation = buildRecommendation(tileItems, cartHandles);
      renderPanel(recommendation);
    });
  }

  return { init: init };

})();